import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, of, map } from 'rxjs';
import { compare } from 'fast-json-patch';

import { AUTH_SERVER_URL } from '../../config';
import {
	ORDER_TRACKING_LIST_SORT,
	SURVEY_SCREEN_NAME_OPTIONS
} from '../models/list';
import {
	UserSettingsInterface,
	ORDER_TRACKING_SORT_LIST,
	AGGREGATED,
} from '../models/settings';
import { User } from '../models/user';
import { BocClient, FieldOfBocClient } from '../models/boc-client-transac';
import { PaginatedResult } from '../models/paginated-result';
import { FieldOfUserInfo, UserInfo } from '../models/user-info';
import { UserBasics } from '../models/user-basics';
import { UserSurveyAnswerDialogData } from '../components/survey/user-survey-answer-dialog-data';
import { ParameterAvailableValues } from '../../user-management/models/v2/parameter-available-values';
import { ParameterIdResponse } from '../../user-management/models/v2/parameter-id-response';
import { UserDatacenterView } from '../models/datacenter-aggrid-view';
import { Services } from '../../users/models/user';
import { BusinessType } from '../models/business-type';

import { AnalyticsService } from './analytics.service';
import { AUTH_GET_USER_DATA_URL } from './connection.common';
import { ConnectionService } from './connection.service';

const AUTH_UPDATE_LANGUAGE = `${ AUTH_SERVER_URL }/user/changeLanguage`;
const AUTH_UPDATE_USER_SETTINGS = `${ AUTH_SERVER_URL }/user/updateUserSettings`;
const AUTH_BOC_URL = `${ AUTH_SERVER_URL }/user/bocs`;
const AUTH_UPDATE_USER_WIDGETS = `${ AUTH_SERVER_URL }/user/updateUserWidgets`;
const AUTH_GET_USERS = `${ AUTH_SERVER_URL }/v2/users`;
const AUTH_SAVE_USER_SURVEY = `${ AUTH_SERVER_URL }/user/userSurvey:save`;
const AUTH_V2 = `${ AUTH_SERVER_URL }/v2`;
const BOCS_URL = `${ AUTH_SERVER_URL }/v3/bocs`;

const USER_KEY = 'copo-user';

interface IUserService {
	user: User;
}

@Injectable({ providedIn: 'root' })
export class UserService implements IUserService, OnDestroy {
	loggedUser: User | undefined;

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly connectionService: ConnectionService,
		private readonly analyticsService: AnalyticsService
	) {
		connectionService.tokenObtainedTask = this.tokenObtainedTask;
		connectionService.tokenDestroyedTask = this.tokenDestroyedTask;
		connectionService.forbiddenAccessRefreshTask = this.forbiddenAccessRefreshTask;
	}

	get user() {
		if (!this.loggedUser) {
			throw new Error('user not logged');
		}
		return this.loggedUser;
	}

	tokenObtainedTask = async (path: string, lastRequestedPath?: string) => {
		this.connectionService.awaitUserData = true;
		try {
			const userIsNewOrUnchanged = await this.updateUserData();
			const user = this.loggedUser;
			if (user) {
				if (!userIsNewOrUnchanged) {
					await this.connectionService.emitDisconnected();
				}
				await this.connectionService.emitConnected(user);

				// call redirect page to force state refresh
				await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });

				if (lastRequestedPath && lastRequestedPath !== path) {
					await this.router.navigate(
						['redirect'],
						{ queryParams: { path: lastRequestedPath }, replaceUrl: false }
					);
				}
			}
		} finally {
			this.connectionService.awaitUserData = false;
		}
	};

	tokenDestroyedTask = async () => {
		localStorage.removeItem(USER_KEY);
		this.loggedUser = undefined;
		await this.connectionService.emitDisconnected();
	};

	forbiddenAccessRefreshTask = () => {
		this.updateUserData().catch((err: unknown) => {
			console.error('error while refreshing user data after forbidden access', err);
		});
	};

	ngOnDestroy() {
		this.connectionService.tokenObtainedTask = undefined;
		this.connectionService.tokenDestroyedTask = undefined;
		this.connectionService.forbiddenAccessRefreshTask = undefined;
	}

	async updateLoggedUser() {
		this.loggedUser = await this.getSavedUser();
		if (this.loggedUser) {
			this.loggedUser.datacenterViews = this.verifyUserDatacenterViews(this.loggedUser.datacenterViews);
		}
		this.analyticsService.setUserContext(this.loggedUser);
		return this.loggedUser;
	}

	verifyUserDatacenterViews(dataCenterViews: UserDatacenterView[]) {
		for (const view of dataCenterViews) {
			if (view.sortModel && view.sortModel.length > 5) {
				view.sortModel.splice(5, view.sortModel.length - 5);
			}
		}
		return dataCenterViews;
	}

	async getSavedUser() {
		return new Promise<User | undefined>(resolve => {
			const userStr = localStorage.getItem(USER_KEY);
			if (!userStr) {
				resolve(undefined);
				return;
			}
			const obj = <User> JSON.parse(userStr);
			resolve(new User(
				obj.id,
				obj.firstname,
				obj.lastname,
				obj.gender,
				obj.language,
				obj.email,
				obj.phone,
				obj.city,
				obj.currentVisibility,
				obj.internal,
				obj.visibilities,
				obj.services,
				obj.settings,
				obj.widgets,
				obj.soldTos,
				obj.shipTos,
				obj.payers,
				obj.previousLastConnectionDate,
				obj.datacenterViews,
				obj.visibleOrganizations,
				obj.soldToCallableAtReshipper,
				obj.organizationRights,
				obj.simulatorUser,
				obj.readOnly,
				obj.bocAvailableValues,
				obj.dateFormat,
				obj.timeFormat,
				obj.initialOrganizationPersonMember,
				obj.blockedServices
			));
		});
	}

	async updateUserData() {
		if (!navigator.onLine) {
			throw new Error('not online');
		}
		const url = new URL(AUTH_GET_USER_DATA_URL);
		const params = url.searchParams;
		params.append('require_user_type', 'external');
		params.append('need_basic_data', 'true');
		params.append('need_all_visibilities', 'true');
		params.append('need_all_services', 'true');
		params.append('need_services_parameters', 'true');
		params.append('need_services_roles', 'true');
		params.append('need_widgets', 'true');
		params.append('need_datacenter_views', 'true');
		params.append('need_settings', 'true');
		params.append('need_current_visibility_data', 'true');
		params.append('need_visible_organizations', 'true');
		params.append('need_soldtos_reshipper', 'true');
		params.append('need_boc_available_values', 'EOFFR');
		params.append('need_boc_available_values', 'EORDR');
		params.append('need_boc_available_values', 'PFAST');
		params.append('need_rights_organizations', 'true');
		params.append('need_blocked_services', 'true');
		const userObject = await firstValueFrom(this.http.get<User>(url.toString()));
		await this.updateLoggedUser();
		const oldUser = this.loggedUser;
		const oldDownloadType = oldUser?.getSettings('download')?.value.type;
		const downloadSetting = userObject.settings.find(setting => setting.key === 'download');
		if (!downloadSetting) {
			userObject.settings.push({
				key: 'download',
				value: {
					version: 1,
					type: oldDownloadType || AGGREGATED
				}
			});
		} else {
			const currentType = (<{ type?: string }>downloadSetting.value).type;
			if (!currentType) {
				downloadSetting.value = {
					...downloadSetting.value,
					type: AGGREGATED
				};
			}
		}
		await this.updateUserIfChanged(oldUser, userObject);
		// if the retrieved user is different from the current one, navigate to home and clear history

		return !oldUser
			|| (oldUser.id === userObject.id && oldUser.currentVisibility.id === userObject.currentVisibility.id);
	}

	async updateUser(userObject: User) {
		return this.updateUserIfChanged(await this.getSavedUser(), userObject);
	}

	async updateUserIfChanged(oldUser: User | undefined, userObject: User) {
		if (!oldUser) {
			localStorage.setItem(USER_KEY, JSON.stringify(userObject));
			await this.updateLoggedUser();
			return;
		}

		const changes = compare(oldUser, userObject);
		if (changes.length === 0) {
			return;
		}
		localStorage.setItem(USER_KEY, JSON.stringify(userObject));
		await this.updateLoggedUser();
	}

	async updateUserVisibility(visibilityId: number) {
		await this.connectionService.requestLogout(
			false,
			{ userId: this.user.id, visibilityId, readOnly: this.user.readOnly }
		);
		this.analyticsService.trackEvent('changeVisibility'); // APP INSIGHTS
	}

	async updateUserSettings(user: User) {
		if (!navigator.onLine) {
			return;
		}
		const { settings } = user;
		const settingsRequest: UserSettingsInterface[] = [];

		for (const setting of settings) {
			settingsRequest.push({ key: setting.key, value: JSON.stringify(setting.value) });
		}

		await firstValueFrom(this.http
			.post(AUTH_UPDATE_USER_SETTINGS, {
				userSettings: settingsRequest
			}));
	}

	async updateSettings() {
		if (!navigator.onLine) {
			return;
		}
		await this.updateUserSettings(this.user);
	}

	async setCurrentOrderTrackingSortLocally(orderTrackingSortOption: keyof typeof ORDER_TRACKING_LIST_SORT) {
		const { user } = this;

		const orderTrackingSortList = user.getSettings('ordertrackingsortlist');

		if (orderTrackingSortList === undefined) {
			user.settings.push({
				key: ORDER_TRACKING_SORT_LIST, value: { version: 1, ordertrackingsortlist: orderTrackingSortOption }
			});
		} else {
			orderTrackingSortList.value = { version: 1, ordertrackingsortlist: orderTrackingSortOption };
		}

		localStorage.setItem(USER_KEY, JSON.stringify(user));
		await this.updateLoggedUser();
	}

	async setCurrentScanModeLocally(
		scanMode: 'matrix' | 'laser' | undefined,
		beep: boolean | undefined,
		displayTip: boolean | undefined
	) {
		const { user } = this;

		const scan = user.getSettings('scan');

		if (scan === undefined) {
			user.settings.push({
				key: 'scan',
				value: {
					version: 1,
					scanMode: scanMode || 'matrix',
					beep: beep !== undefined ? beep : true,
					displayTip: displayTip !== undefined ? displayTip : true
				}
			});
		} else {
			if (scanMode !== undefined) {
				scan.value.scanMode = scanMode;
			}
			if (beep !== undefined) {
				scan.value.beep = beep;
			}
			if (displayTip !== undefined) {
				scan.value.displayTip = displayTip;
			}
		}

		localStorage.setItem(USER_KEY, JSON.stringify(user));
		await this.updateLoggedUser();
	}

	async setCurrentSurveyLocally(
		surveyPageOption: keyof typeof SURVEY_SCREEN_NAME_OPTIONS,
		isAnswered: boolean
	) {
		const { user } = this;

		const survey = user.getSettings('survey');

		if (survey === undefined) {
			user.settings.push({
				key: 'survey',
				value: {
					version: 1,
					page: surveyPageOption,
					isAnswered: isAnswered || false,
				}
			});
		} else {
			survey.value = { version: 1, page: surveyPageOption, isAnswered: isAnswered || false };
		}
		localStorage.setItem(USER_KEY, JSON.stringify(user));
		await this.updateLoggedUser();
	}

	async updateLanguage(lang: string) {
		if (!navigator.onLine) {
			return;
		}

		await firstValueFrom(this.http.post(AUTH_UPDATE_LANGUAGE, {
			language: lang
		}));
	}

	getSoldTosTransac(serviceCode: string) {
		if (!this.user.hasService(serviceCode)) {
			return of([]);
		}
		const url = new URL(AUTH_GET_USER_DATA_URL);
		const params = url.searchParams;
		params.append('need_soldtos_services', serviceCode);

		return this.http.get<User>(url.toString())
			.pipe(map(v => v.services.length > 0 ? v.services[0].soldTos : []));
	}

	getSoldToRelationshipGroupsTransac(serviceCode: string, requireService = true, requireSoldToSettings = false) {
		const url = new URL(AUTH_GET_USER_DATA_URL);
		const params = url.searchParams;
		params.append('need_soldtos_services', serviceCode);
		if (!requireService) {
			params.append('require_soldtos_services', 'false');
		}
		if (requireSoldToSettings) {
			params.append('need_soldto_settings', 'true');
		}
		params.append('need_soldtos_services_relationship_groups', 'true');

		return this.http.get<User>(url.toString())
			.pipe(map(v => v.services.length > 0 ? v.services[0].soldToRelationshipGroups : []));
	}

	findBocs(
		fields?: string[],
		filter?: string,
		page?: number,
		limit?: number,
		orderBy?: string,
		onlyCount?: boolean,
		onlyDistinct?: boolean
	) {
		const url = new URL(BOCS_URL);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		if (filter) {
			params.append('filter', filter);
		}
		if (page !== undefined) {
			params.append('page', String(page));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		if (onlyDistinct) {
			params.append('distinct', String(onlyDistinct));
		}
		return this.http.get<PaginatedResult<BocClient>>(url.toString());
	}

	getBocs(
		fields?: FieldOfBocClient[],
		filter?: string,
		page?: number,
		limit?: number,
		orderBy?: (keyof BocClient)[] | keyof BocClient,
		onlyCount?: boolean,
		onlyDistinct?: boolean
	) {
		const dataUrl = new URL(AUTH_BOC_URL);
		const params = dataUrl.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		if (filter) {
			params.append('filter', filter);
		}
		if (page !== undefined) {
			params.append('page', String(page));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (orderBy) {
			params.append('orderBy', typeof orderBy === 'string' ? orderBy : orderBy.join(','));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		if (onlyDistinct) {
			params.append('distinct', String(onlyDistinct));
		}
		return this.http.get<PaginatedResult<BocClient>>(dataUrl.toString());
	}

	saveUserSurvey(userSurvey: UserSurveyAnswerDialogData) {
		if (!navigator.onLine) {
			throw new Error('not online');
		}
		return this.http.post(AUTH_SAVE_USER_SURVEY, userSurvey, { responseType: 'text' });
	}

	async updateDashboardWidgets() {
		if (!navigator.onLine) {
			return;
		}
		await this.updateUserDashboardWidgets(this.user);
	}

	async updateSurvey(surveyScreenName: keyof typeof SURVEY_SCREEN_NAME_OPTIONS, isAnswered: boolean) {
		if (!navigator.onLine) {
			return;
		}
		await this.setCurrentSurveyLocally(surveyScreenName, isAnswered);
		await this.updateSettings();
		await this.updateUserData();
	}

	async updateUserDashboardWidgets(user: User) {
		if (!navigator.onLine) {
			return;
		}
		await firstValueFrom(this.http
			.post(AUTH_UPDATE_USER_WIDGETS, {
				userWidgetResponses: user.widgets.map(v => ({ ...v, content: JSON.stringify(v.content) }))
			}));
	}

	getUserDetailsById(
		userId?: string,
		fields?: FieldOfUserInfo[] | string[]
	) {
		let id = !userId && this.loggedUser ? String(this.loggedUser.id) : userId || '';
		if (id) {
			id = encodeURIComponent(id);
		}
		const dataUrl = new URL(`${ AUTH_GET_USERS }/${ id }`);
		const params = dataUrl.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		return this.http.get<UserInfo>(dataUrl.toString()).pipe(
			map(userInfo => <UserInfo> ({
				...userInfo,
				language: userInfo.language.slice(0, 2)
			}))
		);
	}

	async userToUpdate(userId: number, updatedUser: UserBasics) {
		const url = `${ AUTH_GET_USERS }/:${ userId }`;
		await firstValueFrom(this.http.put(url.toString(), updatedUser));
	}

	getBocAvailableValuesBasedOnCode(serviceCode: string, rqdParameter: string) {
		if (this.user.bocAvailableValues) {
			for (const bocValue of this.user.bocAvailableValues) {
				if (bocValue.code === serviceCode) {
					return bocValue.parameters[rqdParameter];
				}
			}
		}
		return undefined;
	}

	async acceptPersonMemberRole(
		userId: number,
		orgId: number,
	) {
		const url = new URL(`${ AUTH_GET_USERS }/${ userId }/organizations/${ orgId }/validate-role`);
		return firstValueFrom(this.http.put(url.toString(), null));
	}

	async rejectPersonMemberRole(
		userId: number,
		orgId: number,
	) {
		const url = new URL(`${ AUTH_GET_USERS }/${ userId }/organizations/${ orgId }/reject-role`);
		return firstValueFrom(this.http.delete(url.toString()));
	}

	async getParameterValuesList(parameters: number[]) {
		const url = new URL(`${ AUTH_V2 }/parameterValues`);
		const params = url.searchParams;
		for (const parameter of parameters) {
			params.append('parameterIds', parameter.toString());
		}
		return firstValueFrom(this.http.get<ParameterAvailableValues[]>(url.toString()));
	}

	async addOrUpdateServiceParametersOfUser(
		userId: number,
		orgId: number,
		servicesId: string,
		parametersRequest: ParameterIdResponse[]
	) {
		if (parametersRequest.length === 0) {
			return [];
		}
		const url = new URL(
			`${ AUTH_GET_USERS }/${ userId }/organizations/${ orgId }/services/${ servicesId }/parameters`
		);
		return firstValueFrom(this.http.put(url.toString(), parametersRequest));
	}

	async getServices() {
		const url = new URL(`${ AUTH_SERVER_URL }/v3/services`);
		return firstValueFrom(this.http.get<Services[]>(url.toString()));
	}

	async getBusinessTypes(
		soldTo: string
	) {
		const url = new URL(`${ BOCS_URL }/${ soldTo }/businessTypes`);
		return firstValueFrom(this.http.get<BusinessType[]>(url.toString()));
	}
}
